import GameContext from "../../common/context/game-context";
import FootballGameContextSnapshot from "./football-game-context-snapshot";
import SubstitutionIsNotValid from "../exceptions/substitution-is-not-valid";
import DefaultFootballConfig from "../config/default";
import FootballTeamContext from "./football-team-context";
import {
    getFieldDirectionMultiplier,
    getTimeoutsByHalf,
    getTimeoutsByPeriod,
    getYardsToFirstDown,
    halfNumberByPeriod,
    secondHalfPeriodNumber
} from "../utils/context-utils";
import { deepCopy } from "../../../services/common/object-util";

export default class FootballGameContext extends GameContext {
    constructor({
        gameId,
        ruleSetId,
        config = deepCopy(DefaultFootballConfig),
        fieldPosition = 0,
        gameClock = null,
        awayTeamId,
        homeTeamId,
        currentPeriod = 0,
        homeTeamHasPossession = true
    } = {}) {
        super(gameId, ruleSetId, config);

        this.down = 1;
        this.fieldPosition = fieldPosition; //0 Home Team Goal Line - 100 Visitor Team Goal Line
        this.gameClock = gameClock;
        this.homeTeamHasPossession = homeTeamHasPossession;
        this.firstDownAt = fieldPosition + 10;
        this.currentPeriod = currentPeriod;
        this.score = [0, 0];
        this.timeouts = {}; // By period => { 1: { awayTeamId: 0, homeTeamId: 0 }, 2: { awayTeamId: 0, homeTeamId: 0 }, ... }
        this.startSettings = {
            tossWinner: null,
            offenseTeam: null
        };
        this.gameInitialized = false;
        this.awayTeamId = awayTeamId;
        this.homeTeamId = homeTeamId;
        this.triggerPAT = false;
        this.currentDriveId = null;
        this.driveContext = {
            triggerDrive: false,
            previousDriveEndType: null,
            driveTeamId: null
        };
        this.triggerExtraOvertime = false;
        this.homeTeamOvertimeDrive = false;
        this.awayTeamOvertimeDrive = false;
        this.overtimeNotification = null;
        this.swapTeams = false;
        this.orderPlayersByPosition = true;
        this.keepSwappingTeams = false;
        this.triggerSwapTeams = false;
        this.kickingOffTeamId = null;
        this.firstQtrKickoffTeamId = null;
        this.triggerKO = false;
        this.currentPlayerInPossession = null;
        this.fbOtDisabled = false;
        this.coinTossRequired = false;
        // Track whether the current pause was initiated by a play-recording
        // sequence (vs. a manual user pause). Used to decide whether to
        // auto-resume after the play is finalized.
        this.pausedByPlaySequence = false;
        // Which clock implementation this game uses. "manual" (default) =
        // ManualGameClock (static value, set via fragments/rules).
        // "running" = GameClock (delta-computed, ticks via wall-clock).
        // Stable across minification — prefer this over constructor.name checks.
        this.clockType = "manual";
    }

    setCoinTossRequired(required) {
        this.coinTossRequired = required;
    }

    calculateDistance(end, start, direction) {
        if (direction == null) {
            direction = this.getFieldDirectionMultiplier();
        }
        if (start == null) {
            start = this.fieldPosition;
        }
        return (end - start) * direction;
    }

    isHomePossession() {
        return this.homeTeamHasPossession;
    }

    isVisitorPossession() {
        return !this.homeTeamHasPossession;
    }

    getFieldDirectionMultiplier() {
        return getFieldDirectionMultiplier(this);
    }

    setHomePossession() {
        this.homeTeamHasPossession = true;
    }

    setVisitorPossession() {
        this.homeTeamHasPossession = false;
    }

    isInRedZone() {
        let redZone = false;
        const { general } = this.gameConfig;

        redZone =
            redZone ||
            (this.isVisitorPossession() &&
                this.fieldPosition <= general.redZoneLength);
        redZone =
            redZone ||
            (this.isHomePossession() &&
                this.fieldPosition >=
                    general.fieldLength - general.redZoneLength);

        return redZone;
    }

    yardsToFirstDown() {
        return getYardsToFirstDown(this);
    }

    substitute(playerIn, playerOut) {
        if (playerIn.teamId !== playerOut.teamId) {
            throw new SubstitutionIsNotValid();
        }

        this.getTeamContext(playerIn.teamId).substitute(playerIn, playerOut);
    }

    textFieldPosition() {
        if (this.fieldPosition > this.getFieldLength() / 2) {
            return `VISITOR ${this.getFieldLength() - this.fieldPosition}`;
        }
        return `HOME ${this.fieldPosition}`;
    }

    setFieldPosition(fieldPosition) {
        this.fieldPosition = fieldPosition;
    }

    getFieldPosition() {
        return this.fieldPosition;
    }

    getCurrentPeriod() {
        return this.currentPeriod;
    }

    startPeriod() {
        this.currentPeriod = this.currentPeriod + 1;
    }

    setClockSeconds(seconds) {
        this.gameClock.setSeconds(seconds);
    }

    getTime() {
        return this.gameClock.now();
    }

    isATie() {
        return this.getHomeScore() === this.getVisitorScore();
    }

    getHomeScore() {
        return this.score[0];
    }

    getVisitorScore() {
        return this.score[1];
    }

    setHomeScore(score) {
        this.score[0] = score;
    }

    setVisitorScore(score) {
        this.score[1] = score;
    }

    increaseTeamPossessionScore(score) {
        if (this.isHomePossession()) {
            this.setHomeScore(this.getHomeScore() + score);
        }

        if (this.isVisitorPossession()) {
            this.setVisitorScore(this.getVisitorScore() + score);
        }
    }

    increaseTeamScore(teamId, score) {
        if (this.homeTeamId === teamId) {
            this.setHomeScore(this.getHomeScore() + score);
        } else {
            this.setVisitorScore(this.getVisitorScore() + score);
        }
    }

    switchPossession() {
        this.isHomePossession()
            ? this.setVisitorPossession()
            : this.setHomePossession();
    }

    setPossessionForTeamId(teamId) {
        teamId === this.homeTeamId
            ? this.setHomePossession()
            : this.setVisitorPossession();
    }

    getPossessionTeamId() {
        return this.isHomePossession() ? this.homeTeamId : this.awayTeamId;
    }

    generateSnapshot() {
        return new FootballGameContextSnapshot(this);
    }

    isGameInitialized() {
        return this.gameInitialized;
    }

    getOpponent(teamId) {
        return teamId === this.homeTeamId ? this.awayTeamId : this.homeTeamId;
    }

    toggleSecondHalfPossession() {
        const opponentId = this.getOpponent(this.startSettings.offenseTeam.id);

        this.setPossessionForTeamId(opponentId);
    }

    addTimeout(teamId, periodNumber) {
        let periodTimeouts = this.timeouts[periodNumber] || {
            [this.awayTeamId]: 0,
            [this.homeTeamId]: 0
        };
        periodTimeouts[teamId] = periodTimeouts[teamId] + 1;
        this.timeouts[periodNumber] = periodTimeouts;
    }

    getTimeoutsByPeriod(teamId, periodNumber) {
        return getTimeoutsByPeriod(teamId, periodNumber, this);
    }

    getTimeoutsByHalf(teamId, halfNumber) {
        return getTimeoutsByHalf(teamId, halfNumber, this);
    }

    getTeamContext(teamId) {
        if (!this.teamContexts[teamId]) {
            this.teamContexts[teamId] = new FootballTeamContext(teamId);
        }

        return this.teamContexts[teamId];
    }

    getDriveContext() {
        return this.driveContext;
    }

    setDriveContext(driveContext) {
        this.driveContext = Object.assign(this.driveContext, driveContext);
    }

    resetDriveContext() {
        this.setDriveContext({
            triggerDrive: false,
            previousDriveEndType: null,
            driveTeamId: this.getPossessionTeamId()
        });
    }

    resetTriggers() {
        this.triggerExtraOvertime = false;
        this.driveContext.triggerDrive = false;
    }

    getFieldLength() {
        return this.gameConfig.general.fieldLength;
    }

    getOvertimePeriod() {
        if (this.currentPeriod) {
            return this.currentPeriod >
                this.gameConfig.general.periodsInRegulation
                ? this.currentPeriod -
                      this.gameConfig.general.periodsInRegulation
                : 0;
        }

        return 0;
    }

    halfNumber(period) {
        return halfNumberByPeriod(period, this);
    }

    getCurrentHomeTimeouts() {
        return this.getCurrentTimeouts(this.homeTeamId);
    }

    getCurrentAwayTimeouts() {
        return this.getCurrentTimeouts(this.awayTeamId);
    }

    getCurrentTimeouts(teamId) {
        const halfNumber = this.halfNumber(this.currentPeriod);
        if (halfNumber) {
            return this.getTimeoutsByHalf(teamId, halfNumber);
        } else {
            // Overtime
            return this.getTimeoutsByPeriod(teamId, this.currentPeriod);
        }
    }

    isStartOfSecondHalf(period) {
        return period === secondHalfPeriodNumber(this);
    }

    getOvertimeNotification() {
        return this.overtimeNotification;
    }

    setOvertimeNotification(value) {
        this.overtimeNotification = value;
    }

    setFirstDownAt(firstDownAt) {
        if (firstDownAt < 0) this.firstDownAt = 0;
        else if (firstDownAt > this.getFieldLength())
            this.firstDownAt = this.getFieldLength();
        else this.firstDownAt = firstDownAt;
    }

    setCurrentPlayerInPossession(player) {
        this.currentPlayerInPossession = player;
    }

    getCurrentPlayerInPossession() {
        return this.currentPlayerInPossession;
    }
}
